import { applyCustomerViaRpc } from '../../sync/core/flushRpc';
import type { SyncQueueItem } from '../../sync/core/syncTypes';
import { captureOpsFailure } from '../../shared/sentry/sentryOps';
import { refreshWebCustomerSnapshotAfterWrite } from './customerRepositoryShared';
import { adjustWebCustomerBalanceInCache } from './webCustomerSnapshotCache';

async function recordCustomerChargeViaServer(params: {
  customerId: string;
  amount: number;
  note?: string;
  saleId?: string | null;
  /** Idempotente si se reusa (p. ej. payment.id de la venta POS). */
  movementId?: string | null;
  performedByUserId?: string | null;
  originDeviceId?: string | null;
  branchId?: string | null;
  /** Override one-shot de supervisor (Fiado/credit_override). */
  creditOverrideId?: string | null;
}): Promise<string> {
  const movementId = params.movementId?.trim() || crypto.randomUUID();
  const createdAt = new Date().toISOString();
  const syncPayload: Record<string, unknown> = {
    customerId: params.customerId,
    movementId,
    amount: params.amount,
    type: 'charge',
    note: params.note,
    saleId: params.saleId ?? undefined,
    createdAt,
    performedByUserId: params.performedByUserId ?? null,
    originDeviceId: params.originDeviceId ?? null,
    branchId: params.branchId ?? undefined,
    operationId: movementId,
    ...(params.creditOverrideId
      ? { supervisorOverrides: { credit: params.creditOverrideId } }
      : {}),
  };
  const queueItem: SyncQueueItem = {
    id: crypto.randomUUID(),
    entity: 'fiado',
    operation: 'update',
    payload: syncPayload,
    status: 'pending',
    created_at: createdAt,
  };
  const pushResult = await applyCustomerViaRpc(queueItem);
  if (!pushResult.ok) {
    const error = new Error(pushResult.error);
    captureOpsFailure({
      module: 'customers',
      op: 'customer.charge',
      error,
      ingestError: pushResult.error,
      applied: false,
      path: 'web-ram',
      itemId: params.customerId,
      extra: { customerId: params.customerId },
    });
    throw error;
  }
  return movementId;
}

export const recordCustomerCharge = async ({
  customerId,
  amount,
  note,
  saleId,
  movementId: preferredMovementId,
  performedByUserId,
  originDeviceId,
  branchId,
  creditOverrideId,
}: {
  customerId: string;
  amount: number;
  note?: string;
  /** Si el cargo viene de una venta en POS, enlaza el movimiento a esa venta. */
  saleId?: string | null;
  /** Idempotencia web-ram (p. ej. id del pago customer de la venta). */
  movementId?: string | null;
  performedByUserId?: string | null;
  originDeviceId?: string | null;
  /** Sucursal activa (Informes / resúmenes por sucursal). */
  branchId?: string | null;
  creditOverrideId?: string | null;
}) => {
  // ADR-017 Fase 4: ramOnly siempre true — path online only
  // Audit server-side en apply_customer_command (no duplicar desde el cliente).
  const movementId = await recordCustomerChargeViaServer({
    customerId,
    amount,
    note,
    saleId,
    movementId: preferredMovementId,
    performedByUserId,
    originDeviceId,
    branchId,
    creditOverrideId,
  });
  void movementId;
  adjustWebCustomerBalanceInCache(customerId, amount);
  await refreshWebCustomerSnapshotAfterWrite();
};
